/**
 * tm/pages/MobileCheck.tsx — 휴대폰 「확인할 일 · 내가 안내할 일」 카드 (2026-10-22 지도사)
 *  「직원이 결과를 적어 피드백 → 내가 OK 하면 안내 · 너가 안내 / 내가 안내 선택 · 운전 중이라 휴대폰에서」
 *  「심플하고 직관적이어야」 — 카드마다 큰 단추 두세 개(52px) · 읽고 이해할 칸 없음 · 사유는 안 적어도 됨
 *  같은 카드를 직원이 휴대폰으로 볼 때도 쓴다(답 기다려요 · 답 적기 · 안내했어요).
 *  안내받을 분이 없는 일(「알아보고 나에게 결과 알려 줘」 · 2026-10-22)은 「확인했어요」로 바로 끝
 */
import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import * as todosApi from '../api-todos';
import type { Todo } from '../types';
import type { Staff } from '../components/AssigneeChipPicker';
import { G, clamp } from '../portal-ui/tds';
import { NAVY } from './mobileKit';
import { RESULT_MIN, copyText, kstTodayStr, mdOf, realLen, stageOf, staffNames, telDigits, waitChoices } from '../todoCheckView';

interface Props {
  t: Todo;
  myId: string;
  staff: Staff[];
  onChange: (t: Todo) => void;
  show: (msg: string) => void;
}

const big = (fill?: boolean): CSSProperties => ({
  minHeight: 52, padding: '0 12px', borderRadius: 14, border: fill ? 'none' : `1px solid ${G.g400}`, background: fill ? NAVY : '#fff',
  color: fill ? '#fff' : G.g900, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', boxSizing: 'border-box', minWidth: 0,
});
const quiet: CSSProperties = { minHeight: 44, padding: '0 4px', border: 'none', background: 'none', color: G.g600, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' };
const grid = (n: number): CSSProperties => ({ display: 'grid', gridTemplateColumns: `repeat(${n}, minmax(0,1fr))`, gap: 8, marginTop: 12 });
const isIOS = () => typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);

export default function MobileCheck({ t, myId, staff, onChange, show }: Props) {
  const stage = stageOf(t, myId);
  const [mode, setMode] = useState<'' | 'back' | 'wait' | 'answer' | 'notify'>('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const name = staffNames(t, staff);
  const tel = telDigits(t.notify_phone);

  async function run(p: Promise<{ success: boolean; data?: Todo; error?: string }>, msg: string) {
    if (busy) return;
    setBusy(true);
    try {
      const r = await p;
      if (r.success && r.data) { onChange({ ...t, ...r.data }); show(msg); setMode(''); setText(''); }
      else { if (r.data) onChange({ ...t, ...r.data }); show(r.error || '저장하지 못했어요 · 다시 눌러 주세요'); }   // 답이 그사이 고쳐졌으면 새 답으로
    } catch { show('연결이 끊겼어요 · 다시 눌러 주세요'); }
    finally { setBusy(false); }
  }

  const answer = t.result_note
    ? <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 12, background: G.g50, border: `1px solid ${G.g200}`, fontSize: 16, lineHeight: 1.6, color: G.g900, whiteSpace: 'pre-wrap', wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>{t.result_note}</div>
    : null;
  const who = (t.notify_to || t.notify_phone) ? <div style={{ marginTop: 8, fontSize: 15, color: G.g700, ...clamp(1) }}>{t.notify_to || '안내받을 분'} · {t.notify_phone || '연락처 없음'}</div> : null;
  const ask = t.notify_to ? '팀장님께 전할 답' : '알아본 결과';
  const head = (sub: string) => (
    <div>
      <div style={{ fontSize: 17, fontWeight: 700, color: G.g900, lineHeight: 1.4, wordBreak: 'keep-all', ...clamp(2) }}>{t.title}</div>
      <div style={{ marginTop: 2, fontSize: 14, color: G.g600, ...clamp(1) }}>{sub}</div>
    </div>
  );
  const smsHref = tel ? `sms:${tel}${isIOS() ? '&' : '?'}body=${encodeURIComponent(t.result_note || '')}` : '';
  const notifyBtns = (
    <div style={grid(tel ? 3 : 2)}>
      {tel && <a href={`tel:${tel}`} style={big()}>전화</a>}
      {tel ? <a href={smsHref} style={big()}>문자</a> : <button type="button" style={big()} onClick={() => { void copyText(t.result_note || '').then(ok => show(ok ? '답을 복사했어요' : '복사하지 못했어요')); }}>답 복사</button>}
      <button type="button" style={big(true)} disabled={busy} onClick={() => void run(todosApi.markNotified(t.id), '끝났어요')}>안내했어요</button>
    </div>
  );
  const wrap = (children: ReactNode) => <div style={{ padding: '14px 0 16px', borderBottom: `1px solid ${G.g200}` }}>{children}</div>;

  // 답 적기 칸 — 처음 적을 때 · 안내할 차례에 고칠 때(고치면 지도사 다시 확인 · 2026-10-22)
  const answerBox = (
    <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
      <textarea className="tds-in" value={text} onChange={e => setText(e.target.value)} rows={4} placeholder={ask} aria-label={ask}
        style={{ width: '100%', padding: '12px 14px', borderRadius: 14, border: `1px solid ${G.g500}`, fontSize: 16, lineHeight: 1.5, boxSizing: 'border-box', resize: 'none', fontFamily: 'inherit' }} />
      <div style={grid(2)}>
        <button type="button" style={big()} onClick={() => setMode('')}>그만두기</button>
        <button type="button" style={{ ...big(true), opacity: realLen(text) >= RESULT_MIN ? 1 : 0.4 }} disabled={busy || realLen(text) < RESULT_MIN}
          onClick={() => void run(todosApi.writeResult(t.id, { note: text }), stage === 'staffNotify' ? '고친 답을 보냈어요 · 지도사님이 다시 확인해요' : '지도사님께 보냈어요')}>{stage === 'staffNotify' ? '고친 답 보내기' : '보내기'}</button>
      </div>
    </div>
  );

  /* ── 지도사: 확인할 차례 ── */
  if (stage === 'check') return wrap(<>
    {head(`${name} 님 답${t.result_at ? ' · ' + mdOf(t.result_at) : ''}`)}
    {answer}
    {who}
    {mode === 'back' ? (
      <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
        <input className="tds-in" value={text} onChange={e => setText(e.target.value)} placeholder="더 볼 점 (안 적어도 돼요)" aria-label="더 볼 점"
          style={{ width: '100%', height: 52, padding: '0 14px', borderRadius: 14, border: `1px solid ${G.g400}`, fontSize: 16, boxSizing: 'border-box', fontFamily: 'inherit' }} />
        <div style={grid(2)}>
          <button type="button" style={big()} onClick={() => { setMode(''); setText(''); }}>그만두기</button>
          <button type="button" style={big(true)} disabled={busy} onClick={() => void run(todosApi.checkTodo(t.id, { action: 'back', reason: text, seen: t.result_at }), `${name} 님께 돌려보냈어요`)}>돌려보내기</button>
        </div>
      </div>
    ) : (t.notify_to || mode === 'notify') ? (<>
      <div style={grid(2)}>
        <button type="button" style={big(true)} disabled={busy} onClick={() => void run(todosApi.checkTodo(t.id, { action: 'ok', notify_by: 'staff', seen: t.result_at }), `${name} 님이 안내해요`)}>{name} 님이 안내</button>
        <button type="button" style={big(true)} disabled={busy} onClick={() => void run(todosApi.checkTodo(t.id, { action: 'ok', notify_by: 'me', seen: t.result_at }), '내가 안내할 일로 옮겼어요')}>내가 안내</button>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <button type="button" style={quiet} onClick={() => setMode('back')}>다시 봐 주세요</button>
        <button type="button" style={quiet} disabled={busy} onClick={() => void run(todosApi.checkTodo(t.id, { action: 'done', seen: t.result_at }), '확인했어요')}>안내 없이 끝내기</button>
      </div>
    </>) : (<>
      <div style={grid(2)}>
        <button type="button" style={big()} onClick={() => setMode('back')}>다시 봐 주세요</button>
        <button type="button" style={big(true)} disabled={busy} onClick={() => void run(todosApi.checkTodo(t.id, { action: 'done', seen: t.result_at }), '확인했어요')}>확인했어요</button>
      </div>
      <button type="button" style={quiet} onClick={() => setMode('notify')}>팀장님께 안내도 해야 해요</button>
    </>)}
  </>);

  /* ── 지도사 · 직원: 안내할 차례 ── */
  if (stage === 'bossNotify' || stage === 'staffNotify') return wrap(<>
    {head(stage === 'staffNotify' ? `지도사님 확인 · ${t.notify_to || '안내받을 분'}께 안내해 주세요` : `${t.notify_to || '안내받을 분'}께 안내할 차례`)}
    {who}
    {t.check_note && stage === 'staffNotify' && <div style={{ marginTop: 6, fontSize: 15, color: G.g800 }}>지도사님 · {t.check_note}</div>}
    {mode === 'answer' ? answerBox : <>
      {answer}
      {notifyBtns}
      {stage === 'staffNotify' && <button type="button" style={quiet} onClick={() => { setText(t.result_note || ''); setMode('answer'); }}>답 고치기</button>}
    </>}
  </>);

  /* ── 직원: 답 기다려요 · 답 적기 ── */
  if (stage === 'write') return wrap(<>
    {head(`${(staff.find(s => s.id === t.created_by) || { name: '지도사' }).name} 님 요청${t.notify_to ? ' · ' + t.notify_to : ''}`)}
    <div style={{ marginTop: 8 }}>
      {t.result_at && <div style={{ marginBottom: 8, fontSize: 15, color: '#B45309', fontWeight: 600 }}>다시 봐 주세요{t.check_note ? ' · ' + t.check_note : ''}</div>}
      {mode === '' && (
        <div style={grid(2)}>
          <button type="button" style={big()} onClick={() => setMode('wait')}>답 기다려요</button>
          <button type="button" style={big(true)} onClick={() => { setText(t.result_note || ''); setMode('answer'); }}>답 적기</button>
        </div>
      )}
      {mode === 'wait' && (
        <div style={grid(2)}>
          {waitChoices(kstTodayStr()).map(w => (
            <button key={w.label} type="button" style={big()} disabled={busy} onClick={() => void run(todosApi.waitReply(t.id, { where: '', until: w.date }), `${mdOf(w.date)}에 다시 올라와요`)}>{w.label} {mdOf(w.date)}</button>
          ))}
        </div>
      )}
      {mode === 'answer' && answerBox}
      {mode !== '' && mode !== 'answer' && <button type="button" style={quiet} onClick={() => setMode('')}>그만두기</button>}
    </div>
  </>);
  return null;
}
